import { isNullish, partialBind } from 'remeda'
import { P, match } from 'ts-pattern'

import { env } from '#@/platform/image-optimization/lib/env'
import {
  findClosestImageWidth,
  findClosestImageWidthWithSizeJumpHack,
} from '#@/platform/image-optimization/lib/findClosestImageWidth'

const LEGACY_STAGING_BUCKET = 'https://bazaar-bucket-stag.s3.amazonaws.com'
const STAGING_BUCKETS = [
  LEGACY_STAGING_BUCKET,
  'https://stagingmanual-imageoptimi-districtoriginalimagebuc-dy5ent2akxs4',
  'https://staging-media-outputbucketb1e245a7-q8tzckvsmffg.s3.amazonaws.com',
] as const
export const STAGING_CLOUDFRONT_HOST = 'staging-images.district.net'

const LEGACY_PROD_BUCKET = 'https://bazaar-bucket-production.s3.amazonaws.com'
export const PROD_BUCKETS = [
  LEGACY_PROD_BUCKET,
  'https://prodmanual-imageoptimizat-districtoriginalimagebuc-crt18bxfdhes',
  'https://prod-media-outputbucketb1e245a7-c5srapwkslso.s3.amazonaws.com',
] as const
export const PROD_CLOUDFRONT_HOST = 'images.district.net'

export const PEXELS_HOST = 'images.pexels.com'

type ArgsWithSrc = BaseArgs & { src: string }
type BaseArgs = {
  width: number
  radius?: number | `${number}` | undefined
  quality?: number | `${number}` | undefined
  format?: 'webp' | 'png' | 'jpg'
  disableSizeJumpHackOnClassicWeb?: boolean
}

export function getImageSrc(args: ArgsWithSrc): string
export function getImageSrc(args: BaseArgs & { src: null }): null
export function getImageSrc(args: BaseArgs & { src: undefined }): undefined
export function getImageSrc(args: BaseArgs & { src: null | undefined }): null | undefined
export function getImageSrc(args: BaseArgs & { src: string | null | undefined }): string | null | undefined

/**
 * Generates an optimized image URL with specified transformations such as width, quality, and border radius.
 *
 * @param src The source URL of the image. Can be a string, null, or undefined (Required).
 * @param width (Required) The target width of the image in pixels.
 * @param quality (Optional) Image quality (1–100). Defaults to 70.
 * @param radius (Optional) Border radius in pixels for rounded corners.
 */
export function getImageSrc({
  width,
  src,
  disableSizeJumpHackOnClassicWeb,
  ...args
}: BaseArgs & { src: string | null | undefined }): string | undefined | null {
  if (isNullish(src)) {
    return src
  }

  // find the appropriate function to use to convert the src to a CDN link
  const getImageSrc = match(src)
    .returnType<(args: ArgsWithSrc) => string>()
    .with(P.when(isStagingBucket), () => partialBind(getCDNLink, STAGING_CLOUDFRONT_HOST))
    .with(P.when(isProdBucket), () => partialBind(getCDNLink, PROD_CLOUDFRONT_HOST))
    .with(P.when(isStagingCloudfront), () => partialBind(getCDNLink, STAGING_CLOUDFRONT_HOST))
    .with(P.when(isProdCloudfront), () => partialBind(getCDNLink, PROD_CLOUDFRONT_HOST))
    .with(P.when(isPexels), () => updatePexelsLink)
    .otherwise(
      () =>
        // next.js image loader doesn't like it if we return the exact same string, so what we would have done as hashed params
        () =>
          [
            src,
            '#__dio_bailed__',
            `#w=${width}`,
            Object.entries(args).reduce((acc, [key, value]) => `${acc}#${key.slice(0, 1)}=${value}`, ''),
          ].join('')
    )

  // when this was originally implemented we incorrectly thought the reason images look fuzzy was a bug, and not because of
  // device pixel ratio (fixed with srcset)
  const closestWidth = match({
    isPexels: isPexels(src),
    sizeJumpHack: env.NEXT_PUBLIC_DISTRICT_IMAGE_OPTIMIZATION_SIZE_JUMP_HACK && !disableSizeJumpHackOnClassicWeb,
  })
    .with({ isPexels: true }, () => width)
    .with({ sizeJumpHack: true }, () => findClosestImageWidthWithSizeJumpHack(width))
    .with({ sizeJumpHack: false }, () => findClosestImageWidth(width))
    .exhaustive()

  return getImageSrc({
    src,
    width: closestWidth,
    ...args,
  })
}

function getCDNLink(newHost: string, { src, width, quality = 70, radius, format = 'webp' }: ArgsWithSrc): string {
  const newUrl = new URL(src)
  newUrl.host = newHost

  if (src.includes(LEGACY_STAGING_BUCKET) || src.includes(LEGACY_PROD_BUCKET)) {
    newUrl.pathname = `/image${newUrl.pathname}`
  }

  newUrl.searchParams.set('width', width.toString())
  newUrl.searchParams.set('quality', quality.toString())
  if (radius) {
    newUrl.searchParams.set('radius', radius.toString())
  }
  if (!newUrl.pathname.endsWith('.gif')) {
    newUrl.searchParams.set('format', format)
  }

  return newUrl.toString()
}

function updatePexelsLink({ src, width }: ArgsWithSrc) {
  const newUrl = new URL(src)
  if (newUrl.host !== PEXELS_HOST) {
    throw new Error('src is not a Pexels URL')
  }

  newUrl.searchParams.set('w', width.toString())
  newUrl.searchParams.delete('h')
  newUrl.searchParams.delete('q')
  newUrl.searchParams.set('auto', 'compress')
  newUrl.searchParams.set('cs', 'tinysrgb')
  newUrl.searchParams.set('dpr', '1')

  return newUrl.toString()
}

function isStagingBucket(src: string) {
  return STAGING_BUCKETS.some((bucket) => src.startsWith(bucket))
}
function isProdBucket(src: string) {
  return PROD_BUCKETS.some((bucket) => src.startsWith(bucket))
}
function isStagingCloudfront(src: string) {
  return src.includes(STAGING_CLOUDFRONT_HOST)
}
function isProdCloudfront(src: string) {
  return src.includes(PROD_CLOUDFRONT_HOST)
}
function isPexels(src: string) {
  return src.includes(PEXELS_HOST)
}
